/**
 * Admin API service.
 * Provides operations for admin-only endpoints like CosmosDB querying.
 *
 * API Endpoints:
 * - POST /admin/query-cosmosdb - Execute read-only CosmosDB query
 *
 * @module services/adminService
 */

import { httpClient } from './httpClient';

const ENDPOINT = '/admin';

/**
 * Target database for admin queries.
 */
export type AdminQueryDatabaseTarget = 'Global' | 'Cluster';

/**
 * Request payload for querying CosmosDB.
 */
export interface AdminQueryCosmosDbRequest {
  target: AdminQueryDatabaseTarget;
  clusterId?: string;
  containerName: string;
  query: string;
  maxItems?: number;
}

/**
 * Response from a CosmosDB query.
 */
export interface AdminQueryCosmosDbResponse {
  target: AdminQueryDatabaseTarget;
  clusterId?: string;
  results: unknown[];
  count: number;
  requestCharge: number;
}

/**
 * Admin Service
 * Encapsulates admin API operations
 */
export const adminService = {
  /**
   * Execute a read-only CosmosDB query.
   * @param request - The query request parameters
   * @returns Promise<AdminQueryCosmosDbResponse> - Query results
   */
  async queryCosmosDb(request: AdminQueryCosmosDbRequest): Promise<AdminQueryCosmosDbResponse> {
    console.log('[AdminService] Executing CosmosDB query:', {
      target: request.target,
      container: request.containerName,
    });
    try {
      const response = await httpClient.post<AdminQueryCosmosDbResponse>(
        `${ENDPOINT}/query-cosmosdb`,
        request
      );
      console.log('[AdminService] Query returned', response.data.count, 'results');
      return response.data;
    } catch (error) {
      console.error('[AdminService] CosmosDB query failed:', error);
      throw error;
    }
  },
};
